import { ApolloClient } from '@apollo/client';
import { GeneralSettingsType } from 'src/types/settings';
import { GET_COMPANY_SETTINGS } from 'src/api/queries/getCompanySettings';

export const COMPANY_SETTINGS_STORAGE_KEY = 'companySettings';

// Interface for stored company settings including metadata
export interface CompanySettingsData extends GeneralSettingsType {
  timestamp: number; // Milliseconds since epoch
  source?: string; // Which component initiated the fetch
}

// Events for company settings
export const COMPANY_SETTINGS_EVENTS = {
  UPDATED: 'company-settings-updated',
  FETCH_STARTED: 'company-settings-fetch-started',
  FETCH_COMPLETED: 'company-settings-fetch-completed',
  FETCH_FAILED: 'company-settings-fetch-failed',
};

// Track if a fetch is currently in progress
let isFetchInProgress = false;

/**
 * Fetch company settings from the API
 */
export const fetchCompanySettings = async (
  companyId: string,
  apolloClient: ApolloClient<any>,
  source: string = 'unknown'
): Promise<GeneralSettingsType | null> => {
  // If a fetch is already in progress, wait for it to complete
  if (isFetchInProgress) {
    // Return existing settings from localStorage while waiting
    const existingSettings = getStoredCompanySettings();
    if (existingSettings) {
      return existingSettings;
    }

    // Return a promise that will resolve when the fetch completes
    return new Promise((resolve) => {
      const handleFetchCompleted = (event: CustomEvent) => {
        window.removeEventListener(
          COMPANY_SETTINGS_EVENTS.FETCH_COMPLETED,
          handleFetchCompleted as EventListener
        );
        resolve(event.detail);
      };

      window.addEventListener(
        COMPANY_SETTINGS_EVENTS.FETCH_COMPLETED,
        handleFetchCompleted as EventListener
      );
    });
  }

  // Set fetch in progress
  isFetchInProgress = true;
  window.dispatchEvent(
    new CustomEvent(COMPANY_SETTINGS_EVENTS.FETCH_STARTED, { detail: { source } })
  );

  try {
    // Use the correct query - directly fetch company settings
    const { data } = await apolloClient.query({
      query: GET_COMPANY_SETTINGS,
      fetchPolicy: 'network-only', // Force network fetch, bypass cache
    });

    if (data?.getCompanySettings) {
      const company = data.getCompanySettings;
      const settings: GeneralSettingsType = {
        currencyCode: company.shopifyStores[0].currencyCode,
        defaultSafetyStockWeeks: company.settings.defaultSafetyStockWeeks,
        fallbackSupplierDeliveryTime: company.settings.fallbackSupplierDeliveryTime,
        warningThreshold: company.settings.warningThreshold,
        preferredMetric: company.settings.preferredMetric,
        onhandSyncMode: company.settings.onhandSyncMode,
        onhandSyncIntervalHours: company.settings.onhandSyncIntervalHours,
        onhandSyncLastRequestedAt: company.settings.onhandSyncLastRequestedAt,
      };

      // Store settings with metadata
      const settingsData: CompanySettingsData = {
        ...settings,
        timestamp: Date.now(),
        source,
      };

      // Store in localStorage
      storeCompanySettings(settingsData);

      // Reset fetch in progress
      isFetchInProgress = false;
      window.dispatchEvent(
        new CustomEvent(COMPANY_SETTINGS_EVENTS.FETCH_COMPLETED, { detail: settings })
      );

      return settings;
    }

    // Reset fetch in progress
    isFetchInProgress = false;
    window.dispatchEvent(
      new CustomEvent(COMPANY_SETTINGS_EVENTS.FETCH_FAILED, {
        detail: { error: 'No data returned' },
      })
    );

    return null;
  } catch (error) {
    console.error('Error fetching company settings:', error);

    // Reset fetch in progress
    isFetchInProgress = false;
    window.dispatchEvent(
      new CustomEvent(COMPANY_SETTINGS_EVENTS.FETCH_FAILED, { detail: { error } })
    );

    return null;
  }
};

/**
 * Fetch and store company settings in localStorage
 */
export const fetchAndStoreCompanySettings = async (
  companyId: string,
  apolloClient: ApolloClient<any>,
  source: string = 'unknown'
): Promise<CompanySettingsData | null> => {
  try {
    const settings = await fetchCompanySettings(companyId, apolloClient, source);

    if (settings) {
      return getStoredCompanySettings(); // Return stored settings with metadata
    }
    return null;
  } catch (error) {
    console.error('Error in fetchAndStoreCompanySettings:', error);
    return null;
  }
};

/**
 * Store company settings in localStorage
 */
export const storeCompanySettings = (settings: CompanySettingsData): void => {
  localStorage.setItem(COMPANY_SETTINGS_STORAGE_KEY, JSON.stringify(settings));

  // Dispatch event for real-time updates
  window.dispatchEvent(new CustomEvent(COMPANY_SETTINGS_EVENTS.UPDATED, { detail: settings }));
};

/**
 * Get company settings from localStorage
 */
export const getStoredCompanySettings = (): CompanySettingsData | null => {
  try {
    const storedSettings = localStorage.getItem(COMPANY_SETTINGS_STORAGE_KEY);
    if (storedSettings) {
      return JSON.parse(storedSettings);
    }
    return null;
  } catch (error) {
    console.error('Error getting company settings from localStorage:', error);
    return null;
  }
};

/**
 * Update specific company setting
 */
export const updateCompanySetting = <K extends keyof GeneralSettingsType>(
  key: K,
  value: GeneralSettingsType[K]
): void => {
  const settings = getStoredCompanySettings();
  if (settings) {
    const updatedSettings: CompanySettingsData = {
      ...settings,
      [key]: value,
      timestamp: Date.now(), // Update timestamp
    };
    storeCompanySettings(updatedSettings);
  }
};

/**
 * Check if company settings need to be refreshed (older than 24 hours)
 */
export const shouldRefreshCompanySettings = (): boolean => {
  const settings = getStoredCompanySettings();
  if (!settings) return true;

  const timeSinceLastFetch = Date.now() - settings.timestamp;
  const hoursSinceLastFetch = timeSinceLastFetch / (1000 * 60 * 60);

  return hoursSinceLastFetch > 24;
};

/**
 * Clear company settings from localStorage
 */
export const clearCompanySettings = (): void => {
  localStorage.removeItem(COMPANY_SETTINGS_STORAGE_KEY);
  // Dispatch event to notify components that settings were cleared
  window.dispatchEvent(new CustomEvent(COMPANY_SETTINGS_EVENTS.UPDATED, { detail: null }));
};
